import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import PostTranslationTabs from '../components/PostTranslationTabs';
import { POST_STATUSES, POST_STATUS_LABELS } from '../constants';
import { getAdminPost, getPostTranslations, createPost, updatePost, getCategories, getTags } from '../../../services/blogService';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Button } from '../../../components/ui/Button';
import { MultiSelect } from '../../../components/ui/MultiSelect';

function PostEditPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract id from pathname since we don't have proper route params in current setup
  const pathMatch = location.pathname.match(/\/posts\/(\d+)\/edit$/);
  const id = pathMatch ? pathMatch[1] : null;
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [formData, setFormData] = useState({
    slug: '',
    status: 'draft',
    featured_image_url: '',
    is_featured: false,
    translations: [{ language_code: 'tr', title: '', content: '', excerpt: '' }],
    categories: [],
    tags: []
  });

  useEffect(() => {
    // Load categories and tags
    const loadData = async () => {
      try {
        const [categoriesRes, tagsRes] = await Promise.all([
          getCategories({ tree: 'false' }),
          getTags({ limit: 100 })
        ]);

        if (categoriesRes.state && categoriesRes.data) {
          setCategories(categoriesRes.data.categories || []);
        }
        if (tagsRes.state && tagsRes.data) {
          setTags(tagsRes.data.tags || []);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Veriler yüklenirken hata oluştu');
      }
    };

    loadData();

    // Load post if editing
    if (isEditing && id) {
      loadPost();
    }
  }, [location.pathname, id]);

  const loadPost = async () => {
    try {
      setLoading(true);
      const [postResponse, translationsResponse] = await Promise.all([
        getAdminPost(id),
        getPostTranslations(id)
      ]);

      if (postResponse.state && postResponse.data) {
        const post = postResponse.data;

        // Get translations array
        let translations = [];
        if (translationsResponse.state && translationsResponse.data) {
          translations = translationsResponse.data.map(t => ({
            language_code: t.language_code,
            title: t.title || '',
            content: t.content || '',
            excerpt: t.excerpt || '',
            meta_title: t.meta_title || '',
            meta_description: t.meta_description || '',
            meta_keywords: t.meta_keywords || ''
          }));
        }

        // If no translations, use single translation from post response
        if (translations.length === 0 && post.translation) {
          translations = [{
            language_code: post.translation.language_code || 'tr',
            title: post.title || '',
            content: post.content || '',
            excerpt: post.excerpt || '',
            meta_title: post.meta_title || '',
            meta_description: post.meta_description || '',
            meta_keywords: post.meta_keywords || ''
          }];
        }

        // If still no translations, use default
        if (translations.length === 0) {
          translations = [{ language_code: 'tr', title: '', content: '', excerpt: '' }];
        }

        setFormData({
          slug: post.slug || '',
          status: post.status || 'draft',
          featured_image_url: post.featured_image_url || '',
          is_featured: post.is_featured || false,
          translations: translations,
          categories: post.categories?.map(c => c.id) || [],
          tags: post.tags?.map(t => t.id) || []
        });
      }
    } catch (error) {
      console.error('Error loading post:', error);
      toast.error('Post yüklenirken hata oluştu');
      navigate('/panel/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const postData = {
        ...formData,
        categories: formData.categories,
        tags: formData.tags
      };

      if (isEditing) {
        await updatePost(id, postData);
        toast.success('Post başarıyla güncellendi');
      } else {
        await createPost(postData);
        toast.success('Post başarıyla oluşturuldu');
      }

      navigate('/panel/blog');
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error(error.response?.data?.message || 'Post kaydedilirken hata oluştu');
    }
  };

  const categoryOptions = categories.map(c => ({
    id: c.id,
    label: c.translation?.name || c.slug
  }));

  const tagOptions = tags.map(t => ({
    id: t.id,
    label: t.translation?.name || t.slug
  }));

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        <p className="text-zinc-400 text-sm">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8 max-w-5xl mx-auto">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/panel/blog')}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Geri
        </Button>
        <h1 className="text-2xl font-bold text-zinc-100">
          {isEditing ? 'Post Düzenle' : 'Yeni Post Oluştur'}
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="md:col-span-2 space-y-1.5">
          <label className="text-xs text-gray-500 uppercase font-semibold">Slug</label>
          <Input
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            placeholder="post-slug-yaziniz"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs text-gray-500 uppercase font-semibold">Durum</label>
          <Select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          >
            {Object.values(POST_STATUSES).map(status => (
              <option key={status} value={status}>
                {POST_STATUS_LABELS[status]}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex items-center gap-2 pt-6">
          <input
            type="checkbox"
            id="is_featured"
            className="w-4 h-4 rounded border-zinc-800 bg-zinc-950 text-zinc-100 focus:ring-zinc-700"
            checked={formData.is_featured}
            onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
          />
          <label htmlFor="is_featured" className="text-sm font-medium text-zinc-300">
            Öne Çıkan Post
          </label>
        </div>

        <div className="md:col-span-2 space-y-1.5">
          <label className="text-xs text-gray-500 uppercase font-semibold">Öne Çıkan Görsel URL</label>
          <Input
            value={formData.featured_image_url}
            onChange={(e) => setFormData({ ...formData, featured_image_url: e.target.value })}
            placeholder="https://example.com/image.jpg"
          />
        </div>

        <div className="md:col-span-2">
          <PostTranslationTabs
            translations={formData.translations}
            onChange={(translations) => setFormData({ ...formData, translations })}
          />
        </div>

        <div className="md:col-span-2">
          <MultiSelect
            label="Kategoriler"
            options={categoryOptions}
            value={formData.categories}
            onChange={(newValue) => setFormData({ ...formData, categories: newValue })}
            placeholder="Kategori seçin..."
          />
        </div>

        <div className="md:col-span-2">
          <MultiSelect
            label="Etiketler"
            options={tagOptions}
            value={formData.tags}
            onChange={(newValue) => setFormData({ ...formData, tags: newValue })}
            placeholder="Etiket seçin..."
          />
        </div>

        <div className="md:col-span-2 flex justify-end gap-3 pt-6 border-t border-zinc-800">
          <Button variant="ghost" onClick={() => navigate('/panel/blog')}>
            İptal
          </Button>
          <Button onClick={handleSave} className="px-8">
            Kaydet
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PostEditPage;

